import { getCurrentConfig, getApiScope } from '../config';
import { DEFAULT_HEADERS } from '../config/constants';

/**
 * Universal API Service - uses same pattern as working DashboardMasterService
 * All API calls should use this centralized service
 */
class UniversalApiService {

  private getBaseURL(): string {
    const config = getCurrentConfig();
    const baseUrl = config?.apiBaseUrl || process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
    return baseUrl;
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const baseUrl = this.getBaseURL();
    
    // Fix double slash issue - remove trailing slash from baseUrl and leading slash from endpoint
    const cleanBaseUrl = baseUrl.replace(/\/$/, '');
    const cleanEndpoint = endpoint.replace(/^\//, '');
    const url = `${cleanBaseUrl}/${cleanEndpoint}`;
    
    // Request URL constructed
    
    // Build headers starting with defaults
    const headers: Record<string, string> = {
      ...DEFAULT_HEADERS,
    };

    // FIXED: Use config service instead of process.env directly
    const config = getCurrentConfig();
    const authEnabled = config.auth.enableAuthentication;
    
    // Auth check completed
    
    if (authEnabled) {
      try {
        // Acquiring token inline
        const { msalInstance } = await import('../contexts/AuthContext');
        const accounts = msalInstance.getAllAccounts();
        
        if (accounts.length > 0) {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: [getApiScope()], // Use scope from .env
            account: accounts[0]
          });
          headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
          // Auth enabled: Using inline token
        } else {
          // Auth enabled but no accounts available
        }
      } catch (error) {
        console.warn('❌ Auth enabled but inline token acquisition failed:', error);
      }
    } else {
      // Auth disabled
    }

    // Add any additional headers from options
    if (options?.headers) {
      Object.assign(headers, options.headers);
    }

    const response = await fetch(url, {
      ...options,
      headers,
      //credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Public API methods
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// Export singleton instance
export const universalApiService = new UniversalApiService();
export default universalApiService;
